/**
 * AiButton — Icono ✦ inline (Doc IA §1.2 — IA Contextual).
 * Se coloca junto a campos de texto para ofrecer acciones contextuales
 * (corregir, traducir, analizar...).
 *
 * Props:
 *  - feature: 'corregir' | 'traducir' | 'mejorar' | 'analizar' | custom
 *  - value:   valor actual del campo
 *  - onAccept: callback(nuevoValor) — al aceptar la acción
 *  - className opcional
 */
import React, { useState } from 'react';
import { iaApi } from '../services/api';
import { Sparkles, Loader2, Check, X, Languages } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const IDIOMAS = [
  { v: 'en', l: 'Inglés' }, { v: 'fr', l: 'Francés' }, { v: 'pt', l: 'Portugués' },
  { v: 'de', l: 'Alemán' }, { v: 'it', l: 'Italiano' }, { v: 'ca', l: 'Catalán' }, { v: 'es', l: 'Español' },
];

export default function AiButton({ feature = 'corregir', value, onAccept, className = '' }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);   // { original, corregido, diff, traducido }
  const [idioma, setIdioma] = useState('en');

  const abrir = async () => {
    if (!value || value.trim().length < 3) {
      toast.info('Escribe algo primero (mín 3 caracteres)');
      return;
    }
    setOpen(true);
    setResult(null);
    if (feature === 'corregir') await correg();
    // 'traducir' espera selección de idioma
  };

  const correg = async () => {
    setLoading(true);
    try {
      const r = await iaApi.corregirTexto(value);
      setResult(r);
    } catch (e) { toast.error(e.message); setOpen(false); }
    finally { setLoading(false); }
  };

  const traduc = async () => {
    setLoading(true);
    try {
      const r = await iaApi.traducir(value, idioma);
      setResult({ ...r, tipo: 'traduccion' });
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const aceptar = () => {
    if (result?.corregido && onAccept) onAccept(result.corregido);
    setOpen(false);
    setResult(null);
    toast.success('Texto actualizado');
  };

  const Icon = feature === 'traducir' ? Languages : Sparkles;

  return (
    <>
      <button
        type="button"
        onClick={abrir}
        className={`inline-flex items-center justify-center w-6 h-6 rounded transition-colors text-[#7B5EA7] hover:text-[#9B59B6] hover:bg-[#9B59B6]/10 ${className}`}
        title={feature === 'corregir' ? 'Corregir ortografía con IA' : feature === 'traducir' ? 'Traducir con IA' : 'IA'}
        data-testid={`ai-btn-${feature}`}
      >
        <Icon className="w-3.5 h-3.5" />
      </button>

      <Dialog open={open} onOpenChange={(v) => { if (!v) { setOpen(false); setResult(null); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon className="w-4 h-4 text-[#7B5EA7]" />
              {feature === 'corregir' && 'Corrección ortográfica'}
              {feature === 'traducir' && 'Traducir texto'}
            </DialogTitle>
          </DialogHeader>

          {loading && (
            <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#7B5EA7]" /></div>
          )}

          {!loading && feature === 'traducir' && !result && (
            <div className="space-y-3">
              <p className="text-sm text-ink-600">Selecciona idioma destino:</p>
              <Select value={idioma} onValueChange={setIdioma}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{IDIOMAS.map((i) => <SelectItem key={i.v} value={i.v}>{i.l}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={traduc} className="w-full">Traducir</Button>
            </div>
          )}

          {!loading && result?.diff && (
            <div className="space-y-3">
              <div className="text-sm leading-relaxed p-3 bg-ink-50 rounded border border-ink-200" data-testid="diff-view">
                {result.diff.map((seg, i) => {
                  if (seg.tipo === 'equal') return <span key={i}>{seg.texto} </span>;
                  if (seg.tipo === 'del') return <span key={i} className="line-through text-rose-600 bg-rose-50 px-1 rounded">{seg.texto} </span>;
                  if (seg.tipo === 'add') return <span key={i} className="text-emerald-700 bg-emerald-50 px-1 rounded font-medium">{seg.texto} </span>;
                  return null;
                })}
              </div>
              <p className="text-xs text-ink-500">
                <span className="line-through text-rose-600">rojo</span> = texto eliminado ·
                <span className="text-emerald-700 ml-2">verde</span> = texto añadido
              </p>
            </div>
          )}

          {!loading && result?.traducido && (
            <div className="space-y-2">
              <p className="text-xs text-ink-500 uppercase">Traducción</p>
              <div className="p-3 bg-ink-50 rounded border border-ink-200 text-sm whitespace-pre-wrap">
                {result.traducido}
              </div>
              <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(result.traducido); toast.success('Copiado'); }}>
                Copiar
              </Button>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setResult(null); }}>
              <X className="w-3.5 h-3.5 mr-1" /> Cerrar
            </Button>
            {result?.corregido && (
              <Button onClick={aceptar} data-testid="ai-btn-aceptar">
                <Check className="w-3.5 h-3.5 mr-1" /> Aplicar corrección
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
